/* eslint-disable no-unused-vars */
import { useState } from "react";

/* PACKAGES */
import { motion } from "framer-motion";
import * as yup from "yup";
import { useFormik } from "formik";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";

/* ANIMATIONS */
import { animation, containerAnimation } from "config/animation";

/* APIs */
import { postContactUsApi } from "service/api";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* VALIDATION SCHEMA */
const validationSchema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email().required("Email address is required"),
  phoneNumber: yup.string().required("Phone is required"),
});

const RegistrationForm = () => {
  const [formStep, setFormStep] = useState(1);
  const [loading, setLoading] = useState(false);

  /* FORM HANDLING */
  const formHandling = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const obj = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
      };
      setLoading(true);
      postContactUsApi(obj)
        .then((res) => {
          if (res?.status) {
            setFormStep(2);
          } else {
            toast.error(res?.message);
          }
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          toast.error(e?.response?.data?.message);
        })
        .finally(() => {
          formHandling.handleReset();
          formHandling.setFieldValue("phoneNumber", "");
          setLoading(false);
        });
    },
  });

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="h-auto xl:h-screen lg:h-screen bg-white"
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-0 h-auto xl:h-full lg:h-full">
        {/* LEFT SECTION */}
        <motion.div
          variants={animation}
          className="col-span-1 h-auto xl:h-full lg:h-full"
        >
          <div className="hidden xl:flex lg:flex bg-leftBanner w-full h-full relative items-center justify-center">
            <h1 className="w-[90%] text-center text-5xl font-source font-bold text-white leading-snug">
              Unwind Street: The Ultimate Destination for Board Game Lovers in
              San Ramon
            </h1>

            {/* LOGO */}
            <div className="absolute top-5 left-5 bg-white rounded-lg p-2">
              <img
                src={require("../images/logo.png")}
                className="h-20 w-auto object-cover"
                alt="Unwind Street - A board Game Club"
              />
            </div>
          </div>

          <div className="block xl:hidden lg:hidden bg-leftBanner w-full h-full relative pt-5 pb-5 xl:pb-0 lg:pb-0">
            {/* LOGO */}
            <div className="text-center w-fit mx-auto bg-white rounded-lg p-2">
              <img
                src={require("../images/logo.png")}
                className="h-20 w-auto mx-auto object-cover"
                alt="Unwind Street - A board Game Club"
              />
            </div>

            <h1 className="mt-5 w-[90%] mx-auto text-center text-2xl xl:text-5xl lg:text-5xl font-source font-bold text-white leading-snug">
              Unwind Street: The Ultimate Destination for Board Game Lovers in
              San Ramon
            </h1>
          </div>
        </motion.div>

        {/* RIGHT SECTION */}
        <motion.div
          variants={animation}
          className="col-span-1 w-full h-full block xl:flex lg:flex items-center justify-center relative p-2 xl:p-0 lg:p-0"
        >
          {/* STEP ONE */}
          {formStep === 1 && (
            <form
              onSubmit={formHandling.handleSubmit}
              className="mt-3 xl:mt-0 lg:mt-0 w-full xl:w-[65%] lg:w-[65%] mx-auto px-3 xl:px-0 lg:px-0"
            >
              <h2 className="text-xl xl:text-2xl lg:text-2xl font-source font-semibold text-black">
                Ready to roll into a world full of fun and board games?
              </h2>
              <p className="mt-1 text-lg font-source font-normal text-greyText">
                Fill out your information below!
              </p>

              <div className="mt-5">
                <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
                  <div className="col-span-1">
                    <label className="text-base font-source font-normal text-greyText">
                      First name <span className="text-red-400">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={formHandling.values.firstName}
                        onChange={formHandling.handleChange}
                        placeholder="First name"
                        className={`px-5 py-4 w-full block outline-none focus:outline-none ${
                          formHandling.errors.firstName &&
                          formHandling.touched.firstName
                            ? "border border-red-400 focus:border-red-400"
                            : "border border-greyBorder focus:border-logoGreen"
                        } bg-white rounded-md text-base font-source font-medium placeholder:font-normal`}
                      />

                      {formHandling.errors.firstName &&
                        formHandling.touched.firstName && (
                          <p className="mt-2 text-sm font-normal text-red-400 font-source">
                            {formHandling.errors.firstName}
                          </p>
                        )}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <label className="text-base font-source font-normal text-greyText">
                      Last name <span className="text-red-400">*</span>
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={formHandling.values.lastName}
                        onChange={formHandling.handleChange}
                        placeholder="Last name"
                        className={`px-5 py-4 w-full block outline-none focus:outline-none ${
                          formHandling.errors.lastName &&
                          formHandling.touched.lastName
                            ? "border border-red-400 focus:border-red-400"
                            : "border border-greyBorder focus:border-logoGreen"
                        } bg-white rounded-md text-base font-source font-medium placeholder:font-normal`}
                      />

                      {formHandling.errors.lastName &&
                        formHandling.touched.lastName && (
                          <p className="mt-2 text-sm font-normal text-red-400 font-source">
                            {formHandling.errors.lastName}
                          </p>
                        )}
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-base font-source font-normal text-greyText">
                    Email address <span className="text-red-400">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formHandling.values.email}
                      onChange={formHandling.handleChange}
                      placeholder="Email address"
                      className={`px-5 py-4 w-full block outline-none focus:outline-none ${
                        formHandling.errors.email && formHandling.touched.email
                          ? "border border-red-400 focus:border-red-400"
                          : "border border-greyBorder focus:border-logoGreen"
                      } bg-white rounded-md text-base font-source font-medium placeholder:font-normal`}
                    />

                    {formHandling.errors.email &&
                      formHandling.touched.email && (
                        <p className="mt-2 text-sm font-normal text-red-400 font-source">
                          {formHandling.errors.email}
                        </p>
                      )}
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-base font-source font-normal text-greyText">
                    Phone <span className="text-red-400">*</span>
                  </label>
                  <div className="mt-1">
                    <PhoneInput
                      placeholder="Phone"
                      name="phone"
                      className={`${
                        formHandling.errors.phoneNumber &&
                        formHandling.touched.phoneNumber &&
                        "error"
                      }`}
                      defaultCountry="US"
                      countries={["US"]}
                      value={formHandling.values.phoneNumber}
                      onChange={(e) =>
                        formHandling.setFieldValue("phoneNumber", e)
                      }
                    />

                    {formHandling.errors.phoneNumber &&
                      formHandling.touched.phoneNumber && (
                        <p className="mt-2 text-sm font-normal text-red-400 font-source">
                          {formHandling.errors.phoneNumber}
                        </p>
                      )}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="mt-5 w-full rounded-md shadow-none hover:shadow-none bg-logoBlue hover:bg-opacity-70 transition-all duration-300 ease-in-out normal-case text-base py-4 font-source font-medium text-white"
              >
                {loading ? "Please wait.." : "Register"}
              </Button>
            </form>
          )}

          {/* STEP TWO */}
          {formStep === 2 && (
            <div className="w-full xl:w-3/5 lg:w-3/5 mx-auto">
              <img
                src={require("../images/congratulations.png")}
                className="w-auto h-[16vh] mx-auto"
                alt="Congratulations"
              />
              <div className="mt-5">
                <h3 className="text-2xl font-source font-semibold text-black text-center">
                  Congratulations and Welcome to Unwind Street!
                </h3>
                <p className="mt-2 text-lg font-source font-normal text-greyText text-center">
                  You’re officially on your way to an epic weekend of board
                  games, laughter, and unforgettable memories! We’re excited to
                  have you join the fun. You’ll hear from us soon via WhatsApp.
                </p>
              </div>
              <div className="mt-5">
                <p className="text-lg font-source font-normal text-greyText text-center">
                  Get ready to roll the dice – we can’t wait to see you at
                  Unwind Street (
                  <a
                    href="https://maps.app.goo.gl/5s3auTrC4y2pvP226"
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 underline"
                  >
                    Philz Cafe, 6000 Bollinger Canyon Rd Suite 1609, San Ramon,
                    CA 94583
                  </a>
                  ){" "}
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default RegistrationForm;
